import {
  type ApiListingRequest,
  CONSTANTS,
  type Columns,
  type TableToolbarFilterConfig,
} from '@bitstopco/bitstop-theme';

import SmartphoneIcon from '@mui/icons-material/Smartphone';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

import LocationInstalledAtms from '@/components/location/tableColumns/LocationInstalledAtms';
import LocationsDotsMenu from '@/components/location/tableColumns/LocationsDotsMenu';
import Chip from '@/components/ui/Chip';
import TagList from '@/components/ui/TagList';
import TableColumnDate from '@/components/ui/table/TableColumnDate';
import TableColumnLocation from '@/components/ui/table/TableColumnLocation';
import TableHeaderTooltipIcon from '@/components/ui/table/TableHeaderDateTooltipIcon';
import { US_STATE_FILTER_OPTIONS } from '@/constants/app/usStates';
import { validateFilterById } from '@/helpers/api';
import { formatPhoneNumber } from '@/helpers/phone';

import MorePopover from '@/components/ui/MorePopover';
import type { Location, WithTags } from '@/types';
import { ClickableText } from '@bitstopco/bitstop-theme';
import type { ChipProps } from '@mui/material';

export const LOCATION_TYPE_LABELS = { native: 'Native', teller: 'Teller' } as const;

/**
 * Locations top card configuration
 */
export const LOCATIONS_CARDS = {
  total_locations: {
    title: 'Total Locations',
    icon: SmartphoneIcon,
    showFloatingIcon: false,
  },
  locations_with_machines: {
    title: 'Locations with machine',
    icon: SmartphoneIcon,
    showFloatingIcon: false,
  },
};

/**
 * Locations table bulk-selection toolbar actions.
 * Add Tag / Remove Tag (indices 0-1) are handled by useBulkTaggableActions;
 * Create Case (index 2) navigates to the case creation page.
 */
export const LOCATIONS_MULTI_SELECT_CONFIG = [
  {
    label: 'Add Tag',
  },
  {
    label: 'Remove Tag',
  },
  {
    label: 'Create Case',
  },
];

/**
 * Locations columns configuration: The columns configuration will be used to generate the table with the desired data
 * * You can learn more about the Table columns configuration in the Theme Live docs: https://github.com/bitstopco/bitstop-theme#live-docs
 */
export const LOCATIONS_COLUMNS: Columns<WithTags<Location>> = {
  defaultValue: '-',
  labelSx: {
    whiteSpace: 'nowrap',
  },
  valueSx: {
    whiteSpace: 'nowrap',
    maxWidth: '400px',
  },
  items: [
    {
      value: 'location_id',
      label: 'Location',
      valueMapper: (_, data) => <TableColumnLocation data={data} />,
      valueSx: {
        minWidth: 250,
        '& h6': {
          maxWidth: 400,
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      },
    },
    {
      value: 'type',
      label: 'Type',
      valueMapper: (value: Location['type']) => {
        if (!value) return '-';
        return (
          <Chip
            size="small"
            label={LOCATION_TYPE_LABELS[value]}
            color={(value === 'native' ? 'primary' : 'default') as ChipProps['color']}
            component="span"
          />
        );
      },
    },
    {
      value: 'merchant_platforms',
      label: 'Platform',
      valueMapper: (value: Location['merchant_platforms']) => {
        if (!value || value.length === 0) return '-';
        return (
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {value.map((platform) => (
              <Chip
                key={platform.platform_id}
                size="small"
                label={platform.name}
                component="span"
              />
            ))}
          </Stack>
        );
      },
    },
    {
      value: 'street_address',
      label: 'Street Address',
      valueMapper: (value: Location['street_address']) => {
        const displayValue = value || '-';
        return value ? (
          <Tooltip title={value}>
            <Typography noWrap variant="body2" sx={{ maxWidth: 250 }}>
              {displayValue}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ maxWidth: 250 }}>
            {displayValue}
          </Typography>
        );
      },
    },
    {
      value: 'address2',
      label: 'Address 2',
      valueMapper: (value: Location['address2']) => {
        const displayValue = value || '-';
        return value ? (
          <Tooltip title={value}>
            <Typography noWrap variant="body2" sx={{ maxWidth: 250 }}>
              {displayValue}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ maxWidth: 250 }}>
            {displayValue}
          </Typography>
        );
      },
    },
    {
      value: 'city',
      label: 'City',
      valueMapper: (value: Location['city']) => (
        <Typography variant="body2">{value || '-'}</Typography>
      ),
    },
    {
      value: 'state',
      label: 'State',
      valueMapper: (value: Location['state']) => (
        <Typography variant="body2">{value || '-'}</Typography>
      ),
    },
    {
      value: 'zipcode',
      label: 'Zip Code',
      valueMapper: (value: Location['zipcode']) => (
        <Typography variant="body2">{value || '-'}</Typography>
      ),
    },
    {
      value: 'country',
      label: 'Country',
      valueMapper: (value: Location['country']) => (
        <Typography variant="body2">{value || '-'}</Typography>
      ),
    },
    {
      value: 'installed_atms',
      label: 'ATM(s) Installed',
      valueSx: {
        '& h6': {
          maxWidth: 400,
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      },
      valueMapper: (value: Location['installed_atms']) => (
        <LocationInstalledAtms installedAtms={value} />
      ),
      hideSort: true,
    },
    {
      value: 'marketing_status',
      label: 'Published Status',
      valueMapper: (value: Location['marketing_status']) => {
        let color = 'default';
        let label = 'No Status';
        if (value === 'published') {
          color = 'success';
          label = 'Published';
        }
        if (value === 'unpublished') {
          color = 'error';
          label = 'Unpublished';
        }
        if (value === 'suggestion_pending') {
          color = 'warning';
          label = 'Suggestion Pending';
        }
        if (value === 'in_review') {
          color = 'info';
          label = 'In Review';
        }
        return (
          <Box sx={{ display: 'flex' }}>
            <Chip
              color={color as ChipProps['color']}
              label={label}
              sx={{ mx: 'auto', fontWeight: 'bold', fontSize: '12px' }}
            />
          </Box>
        );
      },
    },
    {
      label: 'Tags',
      labelSx: {
        minWidth: '250px',
      },
      valueMapper: (_, data) => (
        <TagList selectedTagIds={data.tag_ids} tagsCollection={data.tags} />
      ),
      hideSort: true,
    },
    {
      value: 'business_contact_name',
      label: 'Business Contact Name',
      valueMapper: (value: Location['business_contact_name']) => {
        if (value) return value;
        return '-';
      },
    },
    {
      value: 'business_contact_phone',
      label: 'Business Contact Phone',
      valueMapper: (value: Location['business_contact_phone']) => {
        return value ? formatPhoneNumber(value) : '-';
      },
    },
    {
      value: 'merchant_owner_phone_number',
      label: 'Owner Contact Phone',
      valueMapper: (value) => {
        if (!value) return <>-</>;
        return <Typography variant="body2">{formatPhoneNumber(value)}</Typography>;
      },
    },
    {
      value: 'merchant_owner_email',
      label: 'Owner Contact Email',
      valueMapper: (value) => {
        if (!value) return <>-</>;
        return <Typography variant="body2">{value}</Typography>;
      },
    },
    {
      value: 'merchant_id',
      label: 'Merchant ID',
      valueMapper: (value: Location['merchant_id']) => {
        if (!value) return '-';
        return (
          <ClickableText
            fixIcon
            text={value}
            name="Merchant ID"
            clipboardValue={value}
            to={`/merchant/${value}`}
            sx={{
              '& span': {
                whiteSpace: 'normal',
                wordBreak: 'break-all',
              },
            }}
          />
        );
      },
    },
    {
      value: 'merchant_company_name',
      label: 'Merchant Company Name',
      valueMapper: (value: Location['merchant_company_name']) => (
        <Typography variant="body2">{value || '-'}</Typography>
      ),
    },
    {
      value: 'merchant_owner_first_name',
      label: 'Owner First Name',
      valueMapper: (value: Location['merchant_owner_first_name']) => (
        <Typography variant="body2">{value || '-'}</Typography>
      ),
    },
    {
      value: 'merchant_owner_last_name',
      label: 'Owner Last Name',
      valueMapper: (value: Location['merchant_owner_last_name']) => (
        <Typography variant="body2">{value || '-'}</Typography>
      ),
    },
    {
      value: 'merchant_classification',
      label: 'Merchant Classification',
      valueMapper: (value: Location['merchant_classification']) => (
        <Typography variant="body2">{value || '-'}</Typography>
      ),
    },
    {
      value: 'merchant_is_serviceable',
      label: 'Merchant Serviceable',
      valueMapper: (value: Location['merchant_is_serviceable']) => {
        if (value === undefined || value === null)
          return <Typography variant="body2">-</Typography>;
        return (
          <Chip
            size="small"
            label={value ? 'Serviceable' : 'Not Serviceable'}
            color={(value ? 'success' : 'default') as ChipProps['color']}
            component="span"
          />
        );
      },
    },
    {
      value: 'created_at',
      label: 'Date',
      labelMapper: (value: string) => <TableHeaderTooltipIcon label={value} />,
      valueMapper: (value: Location['created_at'], { local_time_zone, created_at }) =>
        value && (
          <TableColumnDate
            value={value}
            options={{
              timeZone: 'UTC',
            }}
            auxiliaryDate={{
              value: created_at,
              options: {
                timeZone: local_time_zone,
                normalizeAsUTC: false,
                performUtcToZonedTime: false,
              },
            }}
          />
        ),
    },
    {
      value: 'location_actions',
      label: '',
      valueMapper: (_, location) => (
        <LocationsDotsMenu
          location_id={location.location_id}
          atms={location.installed_atms || []}
          address={location.address}
          place_id={location.place_id}
          location_name={location.location_name}
          marketing_status={location.marketing_status}
        />
      ),
    },
  ],
};

/**
 * Locations filters configuration
 * * LOCATIONS_FILTERS_CONFIG: Table filters configuration. You can learn more about the Theme Table filters here:
 * * https://github.com/bitstopco/bitstop-theme#live-docs
 */
export const LOCATIONS_FILTERS_CONFIG: TableToolbarFilterConfig[] = [
  {
    property: 'created_at',
    operator: 'less_than_or_equal',
    type: 'select-timeframe',
    label: 'Select a time frame',
    placeholder: 'Location Created',
    showFooter: true,
    hideApplyButton: true,
    options: [...CONSTANTS.TIME_FRAME],
  },
  {
    property: 'location_id',
    type: 'select-ids',
    placeholder: 'Location',
    inputPlaceholder: 'Location ID',
    label: 'Location ID',
    // validate: validateFilterById('location'),
    validate: (value: string) => {
      // We want to apply the custom filter for the pick_up_id validation
      const filters: ApiListingRequest = {
        filter: {
          operator: 'and',
          operands: [
            {
              operator: 'equal',
              property: 'location_id',
              value: value,
            },
          ],
        },
        pagination: {
          current_page: 1,
          items_per_page: 1,
          sort_ascending: false,
          sort_by: 'location_id',
        },
      };

      return validateFilterById('location')(filters);
    },
    mapperSelectedValue: (value: string) => `Location ${value}`,
    mapperSelectedValues: (count: number) => `Location ID (${count})`,
  },
  {
    type: 'select',
    searchable: false,
    multiple: true,
    operator: 'equal',
    property: 'marketing_status',
    showFooter: true,
    placeholder: 'Published Status',
    clearText: 'Clear filter',
    label: 'Published Status',
    options: [
      { label: 'Published', value: 'published' },
      { label: 'In Review', value: 'in_review' },
      { label: 'Unpublished', value: 'unpublished' },
      { label: 'Suggestion Pending', value: 'suggestion_pending' },
    ],
  },
  {
    type: 'select',
    searchable: false,
    multiple: true,
    operator: 'equal',
    property: 'type',
    showFooter: true,
    placeholder: 'Type',
    clearText: 'Clear filter',
    label: 'Type',
    options: [
      { label: 'Native', value: 'native' },
      { label: 'Teller', value: 'teller' },
    ],
  },
  {
    type: 'select',
    searchable: false,
    multiple: false,
    operator: 'equal',
    property: 'has_pending_suggestion',
    showFooter: true,
    placeholder: 'Pending Suggestion',
    clearText: 'Clear filter',
    label: 'Pending Suggestion',
    options: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
  },
  {
    type: 'select',
    searchable: true,
    multiple: true,
    operator: 'equal',
    property: 'platform_id',
    showFooter: true,
    placeholder: 'Platform',
    clearText: 'Clear filter',
    label: 'Platform',
    options: [],
    searchPlaceholder: 'Search by platform name',
  },
  {
    property: 'location_name',
    type: 'select-ids',
    placeholder: 'Location Name',
    subtitleText: 'Add location name to filter',
    inputPlaceholder: 'Enter location name',
    label: 'Location Name',
    operator: 'contains',
    validate: async () => null,
    mapperSelectedValue: (value: string) => value,
    mapperSelectedValues: (count: number) => `Location Name (${count})`,
  },
  {
    type: 'select',
    searchable: true,
    multiple: true,
    operator: 'equal',
    property: 'merchant_id',
    showFooter: true,
    placeholder: 'Merchant',
    clearText: 'Clear filter',
    label: 'Merchant',
    options: [],
    searchPlaceholder: 'Search by merchant name',
  },
  {
    property: 'tag_ids',
    operator: 'contains',
    type: 'select',
    label: 'Tags',
    placeholder: 'Tags',
    searchable: true,
    multiple: true,
    showFooter: true,
    options: [],
    searchPlaceholder: 'Search by tag name',
  },
  {
    property: 'street_address',
    type: 'select-ids',
    placeholder: 'Street Address',
    subtitleText: 'Add street address to filter',
    inputPlaceholder: 'Enter street address',
    label: 'Street Address',
    operator: 'contains',
    mapperSelectedValue: (value: string) => value,
    mapperSelectedValues: (count: number) => `Street Address (${count})`,
  },
  {
    property: 'address2',
    type: 'select-ids',
    placeholder: 'Address 2',
    subtitleText: 'Add address 2 to filter',
    inputPlaceholder: 'Enter address 2',
    label: 'Address 2',
    operator: 'contains',
    validate: async () => null,
    mapperSelectedValue: (value: string) => value,
    mapperSelectedValues: (count: number) => `Address 2 (${count})`,
  },
  {
    property: 'city',
    type: 'select-ids',
    placeholder: 'City',
    subtitleText: 'Add city to filter',
    inputPlaceholder: 'Enter city',
    label: 'City',
    operator: 'contains',
    validate: async () => null,
    mapperSelectedValue: (value: string) => value,
    mapperSelectedValues: (count: number) => `City (${count})`,
  },
  {
    property: 'state',
    type: 'select',
    showFooter: true,
    hideApplyButton: true,
    placeholder: 'State',
    clearText: 'Clear filter',
    label: 'State',
    options: US_STATE_FILTER_OPTIONS,
    multiple: true,
  },
  {
    property: 'zipcode',
    type: 'select-ids',
    placeholder: 'Zip Code',
    subtitleText: 'Add zip code to filter',
    inputPlaceholder: 'Enter zip code',
    label: 'Zip Code',
    operator: 'contains',
    validate: async () => null,
    mapperSelectedValue: (value: string) => value,
    mapperSelectedValues: (count: number) => `Zip Code (${count})`,
  },
  {
    property: 'country',
    type: 'select-ids',
    placeholder: 'Country',
    subtitleText: 'Add country to filter',
    inputPlaceholder: 'Enter country',
    label: 'Country',
    operator: 'contains',
    validate: async () => null,
    mapperSelectedValue: (value: string) => value,
    mapperSelectedValues: (count: number) => `Country (${count})`,
  },
  {
    // has_installed_atms is special-cased by athena's LocationService (installed_atms is a
    // derived collection, not a scalar column DynamicFilterBuilder can filter via reflection).
    // "Installed" means currently installed only (no date_time_uninstalled) -- see LocationService.
    type: 'select',
    searchable: false,
    multiple: false,
    operator: 'equal',
    property: 'has_installed_atms',
    showFooter: true,
    placeholder: 'ATMs Installed',
    clearText: 'Clear filter',
    label: 'ATMs Installed',
    options: [
      { label: 'Has machines installed', value: 'true' },
      { label: 'No machines installed', value: 'false' },
    ],
  },
];

/**
 * Unassigned Locations filters configuration
 */
export const UNASSIGNED_LOCATIONS_FILTERS_CONFIG: TableToolbarFilterConfig[] = [
  {
    property: 'created_at',
    operator: 'less_than_or_equal',
    type: 'select-timeframe',
    label: 'Select a time frame',
    placeholder: 'Location Created',
    showFooter: true,
    hideApplyButton: true,
    options: [...CONSTANTS.TIME_FRAME],
  },
  {
    property: 'location_id',
    type: 'select-ids',
    placeholder: 'Location',
    inputPlaceholder: 'Location ID',
    label: 'Location ID',
    operator: 'equal',
    validate: (value: string) => {
      const filters: ApiListingRequest = {
        filter: {
          operator: 'and',
          operands: [
            {
              operator: 'equal',
              property: 'location_id',
              value: value,
            },
          ],
        },
        pagination: {
          current_page: 1,
          items_per_page: 1,
          sort_ascending: false,
          sort_by: 'location_id',
        },
      };

      return validateFilterById('location')(filters);
    },
    mapperSelectedValue: (value: string) => `Location ${value}`,
    mapperSelectedValues: (count: number) => `Location ID (${count})`,
  },
  {
    type: 'select',
    searchable: false,
    multiple: true,
    operator: 'equal',
    property: 'marketing_status',
    showFooter: true,
    placeholder: 'Published Status',
    clearText: 'Clear filter',
    label: 'Published Status',
    options: [
      { label: 'Published', value: 'published' },
      { label: 'In Review', value: 'in_review' },
      { label: 'Unpublished', value: 'unpublished' },
      { label: 'Suggestion Pending', value: 'suggestion_pending' },
    ],
  },
  {
    property: 'tag_ids',
    operator: 'contains',
    type: 'select',
    label: 'Tags',
    placeholder: 'Tags',
    searchable: true,
    multiple: true,
    showFooter: true,
    options: [],
    searchPlaceholder: 'Search by tag name',
  },
  {
    property: 'street_address',
    type: 'select-ids',
    placeholder: 'Street Address',
    subtitleText: 'Add street address to filter',
    inputPlaceholder: 'Enter street address',
    label: 'Street Address',
    operator: 'contains',
    mapperSelectedValue: (value: string) => value,
    mapperSelectedValues: (count: number) => `Street Address (${count})`,
  },
  {
    property: 'address2',
    type: 'select-ids',
    placeholder: 'Address 2',
    subtitleText: 'Add address 2 to filter',
    inputPlaceholder: 'Enter address 2',
    label: 'Address 2',
    operator: 'contains',
    validate: async () => null,
    mapperSelectedValue: (value: string) => value,
    mapperSelectedValues: (count: number) => `Address 2 (${count})`,
  },
  {
    property: 'city',
    type: 'select-ids',
    placeholder: 'City',
    subtitleText: 'Add city to filter',
    inputPlaceholder: 'Enter city',
    label: 'City',
    operator: 'contains',
    validate: async () => null,
    mapperSelectedValue: (value: string) => value,
    mapperSelectedValues: (count: number) => `City (${count})`,
  },
  {
    property: 'state',
    type: 'select',
    showFooter: true,
    hideApplyButton: true,
    placeholder: 'State',
    clearText: 'Clear filter',
    label: 'State',
    options: US_STATE_FILTER_OPTIONS,
    multiple: true,
  },
  {
    property: 'zipcode',
    type: 'select-ids',
    placeholder: 'Zip Code',
    subtitleText: 'Add zip code to filter',
    inputPlaceholder: 'Enter zip code',
    label: 'Zip Code',
    operator: 'contains',
    validate: async () => null,
    mapperSelectedValue: (value: string) => value,
    mapperSelectedValues: (count: number) => `Zip Code (${count})`,
  },
  {
    property: 'country',
    type: 'select-ids',
    placeholder: 'Country',
    subtitleText: 'Add country to filter',
    inputPlaceholder: 'Enter country',
    label: 'Country',
    operator: 'contains',
    validate: async () => null,
    mapperSelectedValue: (value: string) => value,
    mapperSelectedValues: (count: number) => `Country (${count})`,
  },
  {
    // has_installed_atms is special-cased by athena's LocationService (installed_atms is a
    // derived collection, not a scalar column DynamicFilterBuilder can filter via reflection).
    // "Installed" means currently installed only (no date_time_uninstalled) -- see LocationService.
    type: 'select',
    searchable: false,
    multiple: false,
    operator: 'equal',
    property: 'has_installed_atms',
    showFooter: true,
    placeholder: 'ATMs Installed',
    clearText: 'Clear filter',
    label: 'ATMs Installed',
    options: [
      { label: 'Has machines installed', value: 'true' },
      { label: 'No machines installed', value: 'false' },
    ],
  },
];

/**
 * Unassigned Locations columns configuration: The columns configuration will be used to generate the table with the desired data
 * * You can learn more about the Table columns configuration in the Theme Live docs: https://github.com/bitstopco/bitstop-theme#live-docs
 */
export const unassignedLocationsColumns = (
  onViewDetails: (locationId: number) => void,
): Columns<WithTags<Location>> => ({
  defaultValue: '-',
  items: [
    {
      value: 'location_id',
      label: 'Location',
      valueMapper: (_, data) => <TableColumnLocation data={data} />,
      valueSx: {
        minWidth: 250,
        '& h6': {
          maxWidth: 400,
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      },
    },
    {
      value: 'location_name',
      label: 'Location Name',
    },
    {
      value: 'street_address',
      label: 'Street Address',
      valueMapper: (value: string) => (
        <Tooltip title={value}>
          <Typography noWrap variant="body2" sx={{ maxWidth: 250 }}>
            {value || '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      value: 'address2',
      label: 'Address 2',
      valueMapper: (value: string) => (
        <Tooltip title={value}>
          <Typography noWrap variant="body2" sx={{ maxWidth: 250 }}>
            {value || '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      value: 'city',
      label: 'City',
      valueMapper: (value: string) => <Typography variant="body2">{value || '-'}</Typography>,
    },
    {
      value: 'state',
      label: 'State',
      valueMapper: (value: string) => <Typography variant="body2">{value || '-'}</Typography>,
    },
    {
      value: 'zipcode',
      label: 'Zip Code',
      valueMapper: (value: string) => <Typography variant="body2">{value || '-'}</Typography>,
    },
    {
      value: 'country',
      label: 'Country',
      valueMapper: (value: string) => <Typography variant="body2">{value || '-'}</Typography>,
    },
    {
      value: 'merchant_is_serviceable',
      label: 'Serviceable',
      valueMapper: (value: boolean) => (
        <Chip
          size="small"
          label={value ? 'Serviceable' : 'Not Serviceable'}
          color={(value ? 'success' : 'default') as ChipProps['color']}
          component="span"
        />
      ),
    },
    {
      value: 'marketing_status',
      label: 'Marketing Status',
      valueMapper: (value: string) => {
        let color = 'default';
        let label = 'No Status';
        if (value === 'published') {
          color = 'success';
          label = 'Published';
        } else if (value === 'unpublished') {
          color = 'error';
          label = 'Unpublished';
        } else if (value === 'suggestion_pending') {
          color = 'warning';
          label = 'Suggestion Pending';
        } else if (value === 'in_review') {
          color = 'info';
          label = 'In Review';
        } else {
          label = value || 'No Status';
        }
        return (
          <Chip size="small" label={label} color={color as ChipProps['color']} component="span" />
        );
      },
    },
    {
      value: 'installed_atms',
      label: 'ATM(s) Installed',
      valueSx: {
        '& h6': {
          maxWidth: 400,
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      },
      valueMapper: (value: Location['installed_atms']) => (
        <LocationInstalledAtms installedAtms={value} />
      ),
      hideSort: true,
    },
    {
      label: 'Tags',
      labelSx: {
        minWidth: '250px',
      },
      valueMapper: (_, data) => (
        <TagList selectedTagIds={data.tag_ids} tagsCollection={data.tags} />
      ),
      hideSort: true,
    },
    {
      value: 'created_at',
      label: 'Created At',
      valueMapper: (value: string) => value && <TableColumnDate value={value} />,
    },
    {
      value: 'actions',
      label: 'Actions',
      valueMapper: (_value: string, locationData: any) => (
        <MorePopover
          items={['View Details']}
          onClick={(index: number) => {
            if (index === 0) {
              onViewDetails(locationData.location_id);
            }
          }}
        />
      ),
    },
  ],
});

// Default export for backwards compatibility
export const UNASSIGNED_LOCATIONS_COLUMNS = unassignedLocationsColumns(() => {});
